import { useFeatureFlagEnabled } from "posthog-js/react"
import { createContext, ReactNode, use } from "react"

/**
 * Feature flags enumeration for type-safety and consistency
 */
export enum FEATURE_FLAGS {
    enableSellMenu = "enableSellMenu",
    brokerStatus = "brokerStatus",
    showSubscription = "showSubscription",
    sellContractFeature = "sellContractFeature"
}

/**
 * Central configuration for feature flags
 * - Default values when PostHog hasn't loaded yet
 * - Used for bootstrap values in PostHogProvider
 */
export const FEATURE_FLAG_CONFIG: Record<FEATURE_FLAGS, boolean> = {
    [FEATURE_FLAGS.enableSellMenu]: false,
    [FEATURE_FLAGS.brokerStatus]: false,
    [FEATURE_FLAGS.showSubscription]: false,
    [FEATURE_FLAGS.sellContractFeature]: false
}

export type SSGFeatureFlags = Partial<Record<FEATURE_FLAGS, boolean>>
// For backward compatibility
const FeatureFlagContext = createContext<SSGFeatureFlags>(FEATURE_FLAG_CONFIG)

export const FeatureFlagProvider = FeatureFlagContext.Provider
/**
 * Hook to check if a specific feature flag is enabled
 */
export function useFeatureFlag(flag: FEATURE_FLAGS | string, defaultValue?: boolean): boolean {
    const ssValues = use(FeatureFlagContext)

    const isEnabled = useFeatureFlagEnabled(flag)
    if (flag in ssValues) {
        return ssValues[flag]
    }
    const defaultVal = defaultValue ?? (flag in FEATURE_FLAG_CONFIG ? FEATURE_FLAG_CONFIG[flag] : false)

    return isEnabled ?? defaultVal
}

/**
 * Convenient access to all feature flags at once
 */
export function useFlags() {
    return {
        [FEATURE_FLAGS.brokerStatus]: useFeatureFlag(FEATURE_FLAGS.brokerStatus),
        [FEATURE_FLAGS.showSubscription]: useFeatureFlag(FEATURE_FLAGS.showSubscription),
        [FEATURE_FLAGS.sellContractFeature]: useFeatureFlag(FEATURE_FLAGS.sellContractFeature)
    }
}

/**
 * Component for conditionally rendering based on feature flags
 */
export function FeatureFlaggedComponent({
    flag,
    children,
    fallback = null,
    defaultValue
}: {
    flag: FEATURE_FLAGS | string
    children: ReactNode
    fallback?: ReactNode
    defaultValue?: boolean
}) {
    const isEnabled = useFeatureFlag(flag, defaultValue)
    return isEnabled ? <>{children}</> : <>{fallback}</>
}
